import type { SprintCapacitySummary, StatusCategory } from '@hygge/shared';

/**
 * The two derivations the capacity band makes from its props.
 *
 * Both used to live inside the component's JSX, where nothing could reach them:
 * every frontend test in this repo is a `.test.ts` over a pure module, so a
 * ternary chain in a `.tsx` is, in practice, untested code. One of the two was
 * already wrong (see `capacityNote`) and the other was one careless edit away
 * from being wrong on two boards at once (see `sprintWorkload`).
 */

// ─── Which sentence stands in for the band ────────────────────────────────

/** Just enough of a sprint to decide whether its capacity can be drawn. */
export interface CapacityNoteSprint {
  name: string;
  startDate: string | null;
  endDate: string | null;
  capacity: Pick<
    SprintCapacitySummary,
    'workingDayCount' | 'headcount' | 'nominalPersonDays'
  > | null;
}

/**
 * The note to show instead of the band, or null when there is a band to show.
 *
 * Zero person-days has two causes and they need different sentences. The old
 * code inferred "nobody is assigned" from `headcount === 0` — but the backend
 * also emptied the roster when the window held no working day, so the two zeros
 * were indistinguishable and the roster message always won. A PM who mis-set a
 * sprint to a weekend was told, on a sprint with fifteen assigned people, that
 * nobody was assigned to it. The window is now asked about first and directly.
 */
export function capacityNote(sprint: CapacityNoteSprint | null): string | null {
  if (!sprint) return 'No sprint is running on this board, so there is no window to measure.';

  const { capacity } = sprint;
  if (!capacity) {
    return `${sprint.name} has no start or end date, so there is no window to measure. Set its dates to see how many working days it actually has.`;
  }

  if (capacity.nominalPersonDays > 0) return null;

  // The dates first: they are the half a reader can fix, and a broken window
  // makes the roster question moot until it is fixed.
  if (capacity.workingDayCount === 0) {
    if (isInverted(sprint.startDate, sprint.endDate)) {
      return `${sprint.name} ends before it starts, so there is no window to measure. Check its dates.`;
    }
    return `${sprint.name} covers no working days — its window falls entirely on a weekend, so there is nothing to measure.`;
  }

  return `No issue in ${sprint.name} is assigned to anyone, so there is no roster to measure.`;
}

function isInverted(startDate: string | null, endDate: string | null): boolean {
  if (!startDate || !endDate) return false;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return end < start;
}

// ─── Open work per person ─────────────────────────────────────────────────

/**
 * Just enough of a sprint issue to weigh it.
 *
 * `category` is `string` and not `StatusCategory` because that is what the wire
 * type says (`SprintIssueItem` in @hygge/shared) — which is one more reason for
 * the comparison to live in one tested place rather than in JSX: the compiler
 * will not catch a typo in it.
 */
export interface WorkloadIssue {
  status: { category: string };
  storyPoints: number | null;
  assignees: { id: string }[];
}

export interface PersonWorkload {
  open: number;
  points: number;
}

/** Categories that are not open work: finished, or abandoned. */
const CLOSED: ReadonlySet<string> = new Set<StatusCategory>(['done', 'cancelled']);

/**
 * Open issues and points per assignee, keyed by employee id.
 *
 * By `status.category`, NEVER by the status name. Measured on this workspace:
 * `task_statuses` spells finished work both `Done` (10 boards) and `DONE`
 * (Account Management, Accountant's + Legal). On those two boards
 * `category = 'done'` matches 2,004 and 158 issues while `name = 'Done'`
 * matches zero — a name check would count every finished issue there as open
 * work and nothing on screen would look wrong.
 *
 * A shared issue counts its points for each assignee, which the strip's
 * footnote says out loud: splitting them would invent fractions nobody
 * estimated.
 */
export function sprintWorkload(issues: readonly WorkloadIssue[]): Map<string, PersonWorkload> {
  const byPerson = new Map<string, PersonWorkload>();

  for (const issue of issues) {
    if (CLOSED.has(issue.status.category)) continue;
    for (const person of issue.assignees) {
      const current = byPerson.get(person.id) ?? { open: 0, points: 0 };
      current.open += 1;
      current.points += issue.storyPoints ?? 0;
      byPerson.set(person.id, current);
    }
  }

  return byPerson;
}
